/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = this.querySelector('.js-search-product-type-value');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.requestVersion = 0;
      this.renderedQueryKey = null;
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
      this.resultsObserver?.disconnect();
      clearTimeout(this.inputTimer);
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', this.handleInputChange.bind(this));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || (this.hasAttribute('open') && this.renderedQueryKey === this.queryKey(this.getQuery())
          && this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)'))
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInputChange() {
      this.close();
      if (!this.getQuery()) {
        this.clearResults();
        return;
      }
      this.inputTimer = setTimeout(() => this.handleInput(), 250);
    }

    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.clearResults();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.close();
      if (this.productTypeInput) this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true' && this.renderedQueryKey === this.queryKey(searchTerm)) {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          if (this.selectOption()) evt.preventDefault();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      this.syncResultRelationships();
      const allResults = Array.from(this.results.querySelectorAll('.predictive-search__item'))
        .filter((item) => !item.closest('[hidden]'));
      const selectedResult = allResults.find((item) => item.getAttribute('aria-selected') === 'true');
      const selectedIndex = allResults.indexOf(selectedResult);
      const nextIndex = key === 'ArrowUp'
        ? (selectedIndex <= 0 ? allResults.length - 1 : selectedIndex - 1)
        : (selectedIndex + 1) % allResults.length;
      const resultToSelect = allResults[nextIndex];

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      if (!this.hasAttribute('open') || this.renderedQueryKey !== this.queryKey(this.getQuery())) return false;
      const selectedResult = this.results.querySelector('.predictive-search__item[aria-selected="true"] > .js-search-link');
      if (!selectedResult) return false;
      selectedResult.click();
      return true;
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    queryKey(searchTerm) {
      return JSON.stringify([searchTerm.trim().toLowerCase(), this.productTypeInput?.value || '']);
    }

    cancelRequest() {
      this.requestVersion += 1;
      this.requestController?.abort();
      this.requestController = null;
    }

    clearResults() {
      this.close();
      this.results.innerHTML = '';
      this.removeAttribute('results');
      this.renderedQueryKey = null;
      this.statusEl.textContent = '';
      this.input.setAttribute('aria-controls', this.results.id);
    }

    async getResults(searchTerm) {
      if (!searchTerm.trim() || this.queryKey(searchTerm) !== this.queryKey(this.getQuery())) return;
      this.cancelRequest();
      const requestVersion = this.requestVersion;
      const queryKey = this.queryKey(searchTerm);
      const controller = new AbortController();
      this.requestController = controller;
      const isCurrent = () => requestVersion === this.requestVersion
        && !controller.signal.aborted && this.getQuery().length > 0
        && queryKey === this.queryKey(this.getQuery());
      this.setLiveRegionLoadingState();
      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';
      const params = new URLSearchParams();
      params.set('q', this.productTypeInput?.value
        ? `product_type:${this.productTypeInput.value} AND ${searchTerm}` : searchTerm);
      params.set('resources[limit]', theme.settings.pSearchLimit);
      params.set('resources[limit_scope]', theme.settings.pSearchLimitScope);
      params.set('resources[options][fields]', searchFields);
      params.set('section_id', 'predictive-search');
      if (this.cachedResults[queryKey]) {
        if (isCurrent()) this.renderResults(this.cachedResults[queryKey], queryKey);
        return;
      }
      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params.toString()}`, {
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Suggestions unavailable');
        const responseText = await response.text();
        if (!isCurrent()) return;
        const tmpl = document.createElement('template');
        tmpl.innerHTML = responseText;
        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        if (!resultsEl) throw new Error('Suggestions unavailable');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);
        this.cachedResults[queryKey] = resultsMarkup;
        if (isCurrent()) this.renderResults(resultsMarkup, queryKey);
      } catch (error) {
        if (!isCurrent() || controller.signal.aborted) return;
        this.close();
        this.setLiveRegionText(this.dataset.errorText);
      } finally {
        if (requestVersion === this.requestVersion) this.requestController = null;
      }
    }

    syncResultRelationships() {
      const lists = this.results.querySelectorAll('.predictive-search__list');
      const visibleIds = [];
      lists.forEach((list, index) => {
        list.id = `${this.input.id}-list-${index}`;
        list.setAttribute('role', 'listbox');
        const panel = list.closest('[role="tabpanel"]');
        if (panel?.getAttribute('aria-labelledby')) {
          list.setAttribute('aria-labelledby', panel.getAttribute('aria-labelledby'));
        } else {
          list.setAttribute('aria-label', this.dataset.suggestionsLabel);
        }
        if (!list.closest('[hidden]')) visibleIds.push(list.id);
      });
      this.results.querySelectorAll('.predictive-search__item').forEach((item, index) => {
        item.id = `${this.input.id}-option-${index}`;
        item.setAttribute('role', 'option');
        if (!item.hasAttribute('aria-selected')) item.setAttribute('aria-selected', 'false');
        if (item.closest('[hidden]')) item.setAttribute('aria-selected', 'false');
      });
      const active = this.results.querySelector('.predictive-search__item[aria-selected="true"]');
      this.input.setAttribute('aria-activedescendant', active?.id || '');
      this.input.setAttribute('aria-controls', visibleIds.join(' ') || this.results.id);
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.removeAttribute('aria-hidden');
      this.statusEl.textContent = statusText || '';
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup, queryKey) {
      this.resultsObserver?.disconnect();
      this.results.innerHTML = resultsMarkup;
      this.renderedQueryKey = queryKey;
      this.syncResultRelationships();
      this.resultsObserver = new MutationObserver(() => this.syncResultRelationships());
      this.resultsObserver.observe(this.results, { subtree: true, attributes: true, attributeFilter: ['hidden'] });
      this.setLiveRegionText(this.dataset.readyText);
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      if (!this.getQuery() || this.renderedQueryKey !== this.queryKey(this.getQuery())) return;
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      this.cancelRequest();
      clearTimeout(this.inputTimer);
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = !this.getQuery().length;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
